import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { OnChangeParams, Option } from 'baseui/select';
import { ApiError, PaginationMeta } from 'common/repositories/common.model';
import { camelizeKeys } from 'humps';
import * as React from 'react';
import { useDebouncedCallback } from 'use-debounce';

import useGetLabelSelect from './use-get-label-select';

interface Options<ListQueryData, ListQueryVariables> {
  useListQueryHook: (
    baseInputs: ListQueryVariables,
    baseOptions?: UseQueryOptions<ListQueryData, ApiError>,
  ) => UseQueryResult<ListQueryData, ApiError>;
  listTransformer: (result: ListQueryData) => Option[];
  paginationTransformer: (result: ListQueryData) => PaginationMeta;
  getMemoizedListVariables: (
    page: number,
    query?: string,
  ) => ListQueryVariables;
  onSelectItem: (value: Option) => void;
  resetPageWhenQueryChanged?: boolean;
  value?: {
    id: string;
    label: string;
  };
  useSelectedValue?: boolean;
  enabledQueryHook?: boolean;
  renderCreate?: boolean;
  createText?: string;
  onClickCreate?: () => void;
}

export default function useSelectInputWithoutDetailHelper<
  ListQueryData,
  ListQueryVariables,
>(helperOptions: Options<ListQueryData, ListQueryVariables>) {
  const {
    onSelectItem,
    useListQueryHook,
    listTransformer,
    paginationTransformer,
    getMemoizedListVariables,
    value,
    resetPageWhenQueryChanged,
    useSelectedValue,
    enabledQueryHook = true,
    renderCreate = false,
    createText = 'Create new',
    onClickCreate = () => {},
  } = helperOptions;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [options, setOptions] = React.useState<Option[]>([]);
  const [selectedOption, setSelectedOption] = React.useState<
    Option | undefined
  >(undefined);
  const [query, setQuery] = React.useState<string | undefined>();
  const [page, setPage] = React.useState(1);

  const {
    isLoading: listLoading,
    data: list,
    error,
    refetch,
  } = useListQueryHook(getMemoizedListVariables(page, query), {
    enabled: enabledQueryHook,
    onSuccess: (result) => {
      if (!useSelectedValue && value) {
        setSelectedOption(value);
      }
      if (page === 1) {
        setOptions(
          listTransformer(
            camelizeKeys(result as any) as unknown as ListQueryData,
          ),
        );
      } else {
        setOptions((prevOptions) =>
          prevOptions.concat(
            listTransformer(
              camelizeKeys(result as any) as unknown as ListQueryData,
            ),
          ),
        );
      }
    },
  });

  React.useEffect(() => {
    if (!value) {
      setSelectedOption(undefined);
    }
  }, [value]);

  const loading = listLoading;

  const onMenuOpen = React.useCallback(() => {
    setQuery(undefined);
    if (error) {
      refetch();
    }
  }, [error, refetch]);

  const pagination = list && paginationTransformer(list);

  const items = React.useMemo(() => {
    const _options = [
      ...(selectedOption
        ? [selectedOption].concat([
            ...options.filter((o) => o.id !== selectedOption?.id),
          ])
        : options),
    ];

    if (pagination && pagination.currentPage < pagination.lastPage) {
      _options.push({
        label: 'Load More...',
        id: 'actionLoad',
        disabled: true,
      });
    }

    if (renderCreate) {
      _options.push({
        label: createText,
        id: 'actionCreate',
        disabled: true,
      });
    }

    return _options;
  }, [createText, options, pagination, renderCreate, selectedOption]);

  const onQueryChange = useDebouncedCallback(
    (event) => {
      resetPageWhenQueryChanged && setPage(1);
      setQuery(event?.target?.value || '');
    },
    800,
    {
      trailing: true,
    },
  );

  const onFocus = React.useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const onBlur = React.useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const _onChange = React.useCallback(
    (params: OnChangeParams) => {
      onSelectItem(params.value);
      setIsMenuOpen(false);
    },
    [onSelectItem],
  );

  const getLabel = useGetLabelSelect({
    loading,
    pagination: list && paginationTransformer(list),
    setPage,
    setIsMenuOpen,
    onClickCreate,
  });

  const _value = React.useMemo(() => {
    if (value) {
      const currentSelected = items.find((find) => find.id === value.id);

      if (currentSelected) {
        return [currentSelected];
      } else {
        return selectedOption ? [selectedOption] : undefined;
      }
    } else {
      return undefined;
    }
  }, [items, selectedOption, value]);

  return {
    value: _value,
    options: items,
    isLoading: loading,
    onOpen: onMenuOpen,
    onInputChange: onQueryChange,
    onFocus,
    onBlur,
    getOptionLabel: getLabel as any,
    getValueLabel: getLabel,
    onChange: _onChange,
    closeOnSelect: false,
    clearable: true,
    maxDropdownHeight: '300px',
    overrides: {
      Popover: {
        props: {
          overrides: {
            Body: {
              style: {
                zIndex: 1000,
              },
            },
          },
          isOpen: isMenuOpen,
          onClickOutside: () => setIsMenuOpen(false),
        },
      },
    },
  };
}
